// Mieterliste (Handoff Mieter §5.1): Status, Portal, Filter mit Zählern, Suche,
// Objekt-Dropdown, Ein- & Auszüge und Monats-Pips. Reine Funktionen – testbar.
// Saldo und Monate kommen ausschließlich aus dem gemeinsamen Mietkonto (LedgerEntry);
// solange es nicht eingerichtet ist, sind sie null und es gibt keinen „Rückstand“.

import { mieteAktuell } from '../immobilien/einheitDetail';
import { mieterName, type MieterDetailRoh } from '../immobilien/objektDetail';
import { objektName, type ObjektKopfRoh } from '../immobilien/objektKopf';
import { normalisiereSuche } from '../immobilien/suche';
import { objektOptionen as objektOptionenAllgemein, type ObjektOption } from '../immobilien/objektOptionen';
import { RUECKSTAND_SCHWELLE } from '../mietkonto/mietkonto';
import type { LedgerEntry } from '../mietkonto/typen';

export interface MieterListenRoh extends MieterDetailRoh {
  objekt_id: string | null;
  kaution?: number | string | null;
  created_at?: string | null;
}

export type MieterStatus = 'inaktiv' | 'einzug' | 'gekuendigt' | 'rueckstand' | 'aktiv';
export type PortalZustand = 'aktiv' | 'eingeladen' | 'gesperrt' | 'kein';
export type ListenFilter = 'aktiv' | 'rueck' | 'wechsel' | 'inaktiv';

export const STATUS_ANZEIGE: Record<MieterStatus, { label: string; ton: 'neutral' | 'info' | 'warn' | 'bad' | 'ok' }> = {
  inaktiv: { label: 'Inaktiv', ton: 'neutral' },
  einzug: { label: 'Einzug geplant', ton: 'info' },
  gekuendigt: { label: 'Gekündigt', ton: 'warn' },
  rueckstand: { label: 'Rückstand', ton: 'bad' },
  aktiv: { label: 'Aktiv', ton: 'ok' },
};

export const PORTAL_ANZEIGE: Record<PortalZustand, { label: string; ton: 'ok' | 'warn' | 'bad' | 'neutral'; farbe: string }> = {
  aktiv: { label: 'Portal aktiv', ton: 'ok', farbe: 'var(--ok)' },
  eingeladen: { label: 'Eingeladen', ton: 'warn', farbe: 'var(--warn)' },
  gesperrt: { label: 'Gesperrt', ton: 'bad', farbe: 'var(--bad)' },
  kein: { label: 'Kein Portal', ton: 'neutral', farbe: 'var(--ink3)' },
};

export const FILTER_LABEL: Record<ListenFilter, string> = {
  aktiv: 'Aktiv', rueck: 'Rückstand', wechsel: 'Ein- & Auszug', inaktiv: 'Inaktiv',
};
export const FILTER_REIHENFOLGE: readonly ListenFilter[] = ['aktiv', 'rueck', 'wechsel', 'inaktiv'];

/** inaktiv, ehemalig und interessent (Entscheidung 13.10.2026) zählen als „Inaktiv“; leerer Status = aktiv. */
export function istInaktiv(status: string | null | undefined): boolean {
  const s = (status ?? '').trim().toLowerCase();
  return s === 'inaktiv' || s === 'ehemalig' || s === 'interessent';
}

export function istGekuendigt(status: string | null | undefined): boolean {
  return (status ?? '').trim().toLowerCase().startsWith('gek');
}

/** Status in der verbindlichen Reihenfolge (README §5.1). */
export function mieterStatus(m: { status: string | null; mietbeginn: string | null }, saldo: number | null, heute: string): MieterStatus {
  if (istInaktiv(m.status)) return 'inaktiv';
  if (m.mietbeginn && m.mietbeginn.slice(0, 10) > heute) return 'einzug';
  if (istGekuendigt(m.status)) return 'gekuendigt';
  if (saldo !== null && saldo < -RUECKSTAND_SCHWELLE) return 'rueckstand';
  return 'aktiv';
}

/**
 * Portal aus profiles.status (Rolle mieter, Zuordnung über E-Mail): Aktiv → aktiv, Inaktiv → gesperrt.
 * „Eingeladen“ gibt es in den Daten noch nicht (Fehlende Daten: Portal-Einladung).
 */
export function portalZustand(profilStatus: string | null | undefined): PortalZustand {
  if (profilStatus === 'Aktiv') return 'aktiv';
  if (profilStatus === 'Inaktiv') return 'gesperrt';
  return 'kein';
}

export type PipFarbe = 'ok' | 'warn' | 'bad' | 'vor';
export interface Pip { monat: string; label: string; farbe: PipFarbe }

/** 12 Monats-Pips aus dem Mietkonto: grün bezahlt, orange teilweise, rot offen, grau ohne Soll (vor Mietbeginn). */
export function monatsPips(konto: Pick<LedgerEntry, 'months'> | null | undefined): Pip[] | null {
  if (!konto) return null;
  return konto.months.map(m => ({
    monat: m.monat,
    label: m.label,
    farbe: m.soll <= 0 ? 'vor' : m.ist >= m.soll - 0.005 ? 'ok' : m.ist > 0 ? 'warn' : 'bad',
  }));
}

/** Zahlungsquote der letzten 12 Monate in % (Ist / Soll der Monate mit Soll); null ohne Soll. */
export function zahlungsquote(konto: Pick<LedgerEntry, 'months'> | null | undefined): number | null {
  if (!konto) return null;
  const mitSoll = konto.months.filter(m => m.soll > 0);
  const soll = mitSoll.reduce((s, m) => s + m.soll, 0);
  if (soll <= 0) return null;
  const ist = mitSoll.reduce((s, m) => s + Math.min(m.ist, m.soll), 0);
  return Math.round((ist / soll) * 100);
}

export interface MieterZeile {
  id: string;
  name: string;
  email: string;
  telefon: string;
  objektId: string | null;
  objektName: string;
  einheit: string;
  kalt: number;
  /** Nebenkosten inkl. Heizung und weiterer Kosten (Spalte „NK“) */
  nk: number;
  gesamt: number;
  kaution: number;
  beginn: string | null;
  ende: string | null;
  status: MieterStatus;
  inaktiv: boolean;
  /** archiviert (Status „ehemalig“) – Anzeige „Ehemalig“ statt „Inaktiv“ */
  ehemalig: boolean;
  portal: PortalZustand;
  /** null = Mietkonto (noch) nicht eingerichtet bzw. kein Konto */
  saldo: number | null;
  pips: Pip[] | null;
  /** normalisiert: Name, E-Mail, Einheit, Objektname */
  suchtext: string;
}

const zahl = (v: unknown): number => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v.replace(',', '.')) : NaN;
  return Number.isFinite(n) ? n : 0;
};

export interface ZeilenEingabe {
  mieter: readonly MieterListenRoh[];
  objekte: readonly ObjektKopfRoh[];
  einheiten: readonly { id: string; bezeichnung: string | null }[];
  /** profiles.status je E-Mail (klein geschrieben) */
  portal: ReadonlyMap<string, string | null>;
  /** Mietkonto je Mieter-ID; null = nicht eingerichtet */
  konten: ReadonlyMap<string, LedgerEntry> | null;
  heute: string;
}

export function baueMieterZeilen(e: ZeilenEingabe): MieterZeile[] {
  const objName = new Map(e.objekte.map(o => [o.id, objektName(o)]));
  const einBez = new Map(e.einheiten.map(x => [x.id, (x.bezeichnung ?? '').trim()]));
  return e.mieter.map(m => {
    const miete = mieteAktuell([m], e.heute);
    const konto = e.konten?.get(m.id) ?? null;
    const saldo = konto ? konto.saldo : null;
    const email = (m.email ?? '').trim();
    const name = mieterName(m);
    const einheit = (m.einheit_id ? einBez.get(m.einheit_id) : '') || (m.einheit ?? '').trim();
    const objekt = m.objekt_id ? objName.get(m.objekt_id) ?? '' : '';
    return {
      id: m.id,
      name,
      email,
      telefon: (m.telefon ?? '').trim() || (m.mobil ?? '').trim(),
      objektId: m.objekt_id,
      objektName: objekt,
      einheit,
      kalt: miete.kalt,
      nk: Math.round((miete.nk + miete.hk + miete.weitere) * 100) / 100,
      gesamt: miete.gesamt,
      kaution: zahl(m.kaution),
      beginn: m.mietbeginn ? m.mietbeginn.slice(0, 10) : null,
      ende: m.mietende ? m.mietende.slice(0, 10) : null,
      status: mieterStatus(m, saldo, e.heute),
      inaktiv: istInaktiv(m.status),
      ehemalig: (m.status ?? '').trim().toLowerCase() === 'ehemalig',
      portal: email ? portalZustand(e.portal.get(email.toLowerCase())) : 'kein',
      saldo,
      pips: monatsPips(konto),
      suchtext: normalisiereSuche(`${name} ${email} ${einheit} ${objekt}`),
    };
  });
}

/** Status-Text: archivierte Mieter als „Ehemalig“. */
export function statusLabel(status: MieterStatus, ehemalig: boolean): string {
  return status === 'inaktiv' && ehemalig ? 'Ehemalig' : STATUS_ANZEIGE[status].label;
}

export function passtFilter(z: Pick<MieterZeile, 'inaktiv' | 'status' | 'saldo'>, f: ListenFilter): boolean {
  switch (f) {
    case 'aktiv': return !z.inaktiv;
    case 'rueck': return !z.inaktiv && z.saldo !== null && z.saldo < -RUECKSTAND_SCHWELLE;
    case 'wechsel': return z.status === 'gekuendigt' || z.status === 'einzug';
    case 'inaktiv': return z.inaktiv;
  }
}

/** Alle Wörter der Suche müssen in Name, E-Mail, Einheit oder Objektname vorkommen. */
export function suchePasst(z: Pick<MieterZeile, 'suchtext'>, suche: string): boolean {
  const q = normalisiereSuche(suche);
  if (!q) return true;
  return q.split(' ').every(w => z.suchtext.includes(w));
}

/** Basis für Filter und Zähler: gewähltes Objekt + Suche. */
export function basisZeilen<T extends Pick<MieterZeile, 'objektId' | 'suchtext'>>(zeilen: readonly T[], objekt: string, suche: string): T[] {
  return zeilen.filter(z => (objekt === 'alle' || z.objektId === objekt) && suchePasst(z, suche));
}

export function zaehleFilter(basis: readonly Pick<MieterZeile, 'inaktiv' | 'status' | 'saldo'>[]): Record<ListenFilter, number> {
  const n: Record<ListenFilter, number> = { aktiv: 0, rueck: 0, wechsel: 0, inaktiv: 0 };
  for (const z of basis) for (const f of FILTER_REIHENFOLGE) if (passtFilter(z, f)) n[f] += 1;
  return n;
}

export interface Kennzahlen {
  /** laufend, ohne Zukunfts-Einzüge */
  aktiv: number;
  sollmiete: number;
  objekte: number;
  einzuegeGeplant: number;
  /** null = Mietkonto nicht eingerichtet */
  rueckstand: number | null;
  rueckstandMieter: number;
  portalAktiv: number;
  portalEingeladen: number;
  /** Anteil aktiver Portalzugänge an allen nicht inaktiven Mietern (0–100) */
  portalQuote: number;
}

export function kennzahlen(zeilen: readonly MieterZeile[], mietkontoEingerichtet: boolean): Kennzahlen {
  const nichtInaktiv = zeilen.filter(z => !z.inaktiv);
  const laufend = nichtInaktiv.filter(z => z.status !== 'einzug');
  const imRueckstand = nichtInaktiv.filter(z => z.saldo !== null && z.saldo < -RUECKSTAND_SCHWELLE);
  const portalAktiv = nichtInaktiv.filter(z => z.portal === 'aktiv').length;
  return {
    aktiv: laufend.length,
    sollmiete: Math.round(laufend.reduce((s, z) => s + z.gesamt, 0) * 100) / 100,
    objekte: new Set(laufend.map(z => z.objektId).filter((id): id is string => !!id)).size,
    einzuegeGeplant: nichtInaktiv.length - laufend.length,
    rueckstand: mietkontoEingerichtet ? Math.round(imRueckstand.reduce((s, z) => s + (z.saldo ?? 0), 0) * 100) / 100 : null,
    rueckstandMieter: imRueckstand.length,
    portalAktiv,
    portalEingeladen: nichtInaktiv.filter(z => z.portal === 'eingeladen').length,
    portalQuote: nichtInaktiv.length ? Math.round((portalAktiv / nichtInaktiv.length) * 100) : 0,
  };
}

export interface Wechsel { id: string; art: 'einzug' | 'auszug'; name: string; objektName: string; einheit: string; datum: string }

/** Die nächsten Ein- und Auszüge ab heute (Einzug = Beginn in der Zukunft, Auszug = gekündigt mit Mietende). */
export function naechsteWechsel(zeilen: readonly MieterZeile[], heute: string, anzahl = 3): Wechsel[] {
  const liste: Wechsel[] = [];
  for (const z of zeilen) {
    if (z.inaktiv) continue;
    const basis = { id: z.id, name: z.name, objektName: z.objektName, einheit: z.einheit };
    if (z.status === 'einzug' && z.beginn) liste.push({ ...basis, art: 'einzug', datum: z.beginn });
    else if (z.status === 'gekuendigt' && z.ende && z.ende >= heute) liste.push({ ...basis, art: 'auszug', datum: z.ende });
  }
  return liste.sort((a, b) => a.datum.localeCompare(b.datum) || a.name.localeCompare(b.name, 'de')).slice(0, anzahl);
}

export type { ObjektOption };

/** Objekte fürs Dropdown; Anzahl = nicht inaktive Mieter je Objekt. */
export function objektOptionen(objekte: readonly ObjektKopfRoh[], zeilen: readonly Pick<MieterZeile, 'objektId' | 'inaktiv'>[]): ObjektOption[] {
  const anzahl = new Map<string, number>();
  for (const z of zeilen) if (!z.inaktiv && z.objektId) anzahl.set(z.objektId, (anzahl.get(z.objektId) ?? 0) + 1);
  return objektOptionenAllgemein(objekte, anzahl);
}

export function istListenFilter(v: string | null): v is ListenFilter {
  return v === 'aktiv' || v === 'rueck' || v === 'wechsel' || v === 'inaktiv';
}
